import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Save, ArrowLeft, Heart, Activity, Flame, TrendingUp } from 'lucide-react';
import { workoutsApi, exercisesApi } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { SetRow } from '@/components/SetLogger';
import { ExercisePickerModal } from '@/components/ExercisePickerModal';
import { cn } from '@/lib/utils';
import { getTodayISO, generateId } from '@/lib/utils';
import type { Exercise, LinkedActivity, MuscleGroup, FilterWorkoutType } from '@/types';
import { FILTER_WORKOUT_TYPES } from '@/types';

interface LocalSet {
  tempId: string;
  exerciseId: string;
  exerciseName?: string;
  setNumber: number;
  reps: number;
  weight: number | null;
}

function LinkedActivityCard({ activities }: { activities: LinkedActivity[] }) {
  return (
    <div className="card p-5 bg-linear-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border border-primary-200 dark:border-primary-800">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        <h2 className="font-semibold text-gray-900 dark:text-white">Garmin-tiedot</h2>
      </div>

      {activities.map((activity) => (
        <div key={activity.id} className="space-y-4">
          {/* Activity name */}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {activity.activityName || activity.activityType}
            {activity.durationMinutes && ` • ${activity.durationMinutes} min`}
          </p>

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Heart Rate */}
            {activity.avgHeartRate && (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                <div className="flex items-center gap-1.5 text-red-500 mb-1">
                  <Heart className="h-4 w-4" />
                  <span className="text-xs font-medium">Syke</span>
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {activity.avgHeartRate} <span className="text-sm font-normal text-gray-500">bpm</span>
                </p>
                {(activity.minHeartRate || activity.maxHeartRate) && (
                  <p className="text-xs text-gray-500">
                    {activity.minHeartRate && `min ${activity.minHeartRate}`}
                    {activity.minHeartRate && activity.maxHeartRate && ' / '}
                    {activity.maxHeartRate && `max ${activity.maxHeartRate}`}
                  </p>
                )}
              </div>
            )}

            {/* Calories */}
            {activity.calories && (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                <div className="flex items-center gap-1.5 text-orange-500 mb-1">
                  <Flame className="h-4 w-4" />
                  <span className="text-xs font-medium">Kalorit</span>
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {activity.calories} <span className="text-sm font-normal text-gray-500">kcal</span>
                </p>
              </div>
            )}

            {/* Aerobic Training Effect */}
            {activity.aerobicTE && (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                <div className="flex items-center gap-1.5 text-blue-500 mb-1">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-xs font-medium">Aerobinen TE</span>
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {Number(activity.aerobicTE).toFixed(1)}
                </p>
                <p className="text-xs text-gray-500">
                  {activity.aerobicTE < 2 ? 'Palauttava' :
                   activity.aerobicTE < 3 ? 'Ylläpitävä' :
                   activity.aerobicTE < 4 ? 'Kehittävä' : 'Erittäin tehokas'}
                </p>
              </div>
            )}

            {/* Anaerobic Training Effect */}
            {activity.anaerobicTE && (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                <div className="flex items-center gap-1.5 text-purple-500 mb-1">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-xs font-medium">Anaerobinen TE</span>
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {Number(activity.anaerobicTE).toFixed(1)}
                </p>
              </div>
            )}
          </div>

          {/* Intensity minutes */}
          {(activity.moderateMinutes || activity.vigorousMinutes) && (
            <div className="flex gap-4 text-sm">
              {activity.moderateMinutes && activity.moderateMinutes > 0 && (
                <span className="text-gray-600 dark:text-gray-400">
                  Kohtuullinen: <span className="font-medium">{activity.moderateMinutes} min</span>
                </span>
              )}
              {activity.vigorousMinutes && activity.vigorousMinutes > 0 && (
                <span className="text-gray-600 dark:text-gray-400">
                  Raskas: <span className="font-medium">{activity.vigorousMinutes} min</span>
                </span>
              )}
            </div>
          )}

          {/* Distance and pace for cardio */}
          {activity.distance && activity.distance > 0 && (
            <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span>
                Matka: <span className="font-medium">{(Number(activity.distance) / 1000).toFixed(2)} km</span>
              </span>
              {activity.durationMinutes && (
                <span>
                  Vauhti: <span className="font-medium">
                    {(() => {
                      const paceSecondsPerKm = (activity.durationMinutes * 60) / (Number(activity.distance) / 1000);
                      const paceMinutes = Math.floor(paceSecondsPerKm / 60);
                      const paceSeconds = Math.round(paceSecondsPerKm % 60);
                      return `${paceMinutes}:${paceSeconds.toString().padStart(2, '0')} min/km`;
                    })()}
                  </span>
                </span>
              )}
              {activity.elevationGain && activity.elevationGain > 0 && (
                <span>
                  Nousu: <span className="font-medium">{activity.elevationGain} m</span>
                </span>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function WorkoutFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [workoutType, setWorkoutType] = useState<FilterWorkoutType | ''>('');
  const [startedAt, setStartedAt] = useState(getTodayISO());
  const [sets, setSets] = useState<LocalSet[]>([]);

  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [recentExerciseIds, setRecentExerciseIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('recentExerciseIds');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const { data: exercisesData } = useQuery({
    queryKey: ['exercises'],
    queryFn: () => exercisesApi.list(),
  });

  const { data: existingWorkout, isLoading: loadingWorkout } = useQuery({
    queryKey: ['workout', id],
    queryFn: () => workoutsApi.get(id!),
    enabled: isEditing,
  });

  useEffect(() => {
    if (existingWorkout) {
      setName(existingWorkout.name || '');
      setNotes(existingWorkout.notes || '');
      setWorkoutType(existingWorkout.workoutType || '');
      setStartedAt(existingWorkout.startedAt?.split('T')[0] || getTodayISO());
      
      const flatSets: LocalSet[] = [];
      if (existingWorkout.exercises) {
        for (const we of existingWorkout.exercises) {
          if (we.sets) {
            for (const s of we.sets) {
              flatSets.push({
                tempId: generateId(),
                exerciseId: we.exerciseId,
                exerciseName: we.exercise?.name,
                setNumber: s.setNumber,
                reps: s.reps ?? 0,
                weight: s.weight ? Number(s.weight) : null,
              });
            }
          }
        }
      }
      setSets(flatSets);
    }
  }, [existingWorkout]);

  // Päättele treenityyppi liikkeiden lihasryhmistä
  const inferWorkoutType = (): FilterWorkoutType => {
    if (sets.length === 0) return 'other';

    const muscleGroups = sets
      .map(s => {
        const exercise = exercisesData?.exercises.find(e => e.id === s.exerciseId);
        return exercise?.muscleGroup;
      })
      .filter(Boolean) as string[];

    if (muscleGroups.length === 0) return 'other';

    // Jos on cardio-liikkeitä
    if (muscleGroups.includes('cardio')) return 'cardio';

    // Jos kaikki ovat jalkaliikkeitä
    if (muscleGroups.every(g => g === 'legs')) return 'legs';

    // Jos kaikki ovat yläkropan liikkeitä
    const upperBodyGroups = ['chest', 'back', 'shoulders', 'arms'];
    if (muscleGroups.every(g => upperBodyGroups.includes(g))) return 'upper';

    // Jos on sekä ylä- että alakropan liikkeitä -> kokovartalo
    const hasUpper = muscleGroups.some(g => upperBodyGroups.includes(g));
    const hasLower = muscleGroups.some(g => g === 'legs');
    if (hasUpper && hasLower) return 'full_body';

    return 'other';
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Jos käyttäjä ei ole valinnut tyyppiä, päättele se automaattisesti
      const finalWorkoutType = workoutType || inferWorkoutType();

      const workoutData = {
        name: name || `Treeni ${new Date(startedAt).toLocaleDateString('fi-FI')}`,
        notes: notes || undefined,
        workoutType: finalWorkoutType,
        startedAt: new Date(startedAt).toISOString(),
      };

      let workout;
      if (isEditing) {
        workout = await workoutsApi.update(id!, workoutData);
        
        // Poista vanhat exercises ennen uusien lisäämistä
        if (existingWorkout?.exercises) {
          for (const we of existingWorkout.exercises) {
            await workoutsApi.removeExercise(workout.id, we.id);
          }
        }
      } else {
        workout = await workoutsApi.create(workoutData);
      }

      // Lisää exercises ja sarjat (sekä uusille että muokatuille)
      if (sets.length > 0) {
        const exerciseGroups = new Map<string, LocalSet[]>();
        for (const set of sets) {
          const existing = exerciseGroups.get(set.exerciseId) || [];
          existing.push(set);
          exerciseGroups.set(set.exerciseId, existing);
        }

        for (const [exerciseId, exerciseSets] of exerciseGroups) {
          const workoutExercise = await workoutsApi.addExercise(workout.id, exerciseId);
          
          for (let i = 0; i < exerciseSets.length; i++) {
            const set = exerciseSets[i]!;
            await workoutsApi.addSet(workout.id, workoutExercise.id, {
              setNumber: i + 1,
              reps: set.reps,
              weight: set.weight ?? undefined,
            });
          }
        }
      }

      return workout;
    },
    onSuccess: (workout) => {
      // Invalidoi sekä treenilista että yksittäinen treeni
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
      queryClient.invalidateQueries({ queryKey: ['workout', workout.id] });
      navigate('/');
    },
  });

  // Luo uusi liike
  const createExerciseMutation = useMutation({
    mutationFn: (data: { name: string; muscleGroup?: MuscleGroup }) =>
      exercisesApi.create(data),
    onSuccess: (newExercise) => {
      queryClient.invalidateQueries({ queryKey: ['exercises'] });
      // Lisää uusi sarja suoraan treeniin
      addSet(newExercise);
      setShowExerciseModal(false);
    },
  });

  const handleCreateExercise = (name: string, muscleGroup?: MuscleGroup) => {
    createExerciseMutation.mutate({ name, muscleGroup });
  };

  // Save recent exercise to localStorage
  const saveRecentExercise = (exerciseId: string) => {
    const updated = [exerciseId, ...recentExerciseIds.filter(id => id !== exerciseId)].slice(0, 10);
    setRecentExerciseIds(updated);
    try {
      localStorage.setItem('recentExerciseIds', JSON.stringify(updated));
    } catch {
      // Ignore localStorage errors
    }
  };

  const addSet = (exercise: Exercise) => {
    const lastSet = sets.filter(s => s.exerciseId === exercise.id).pop();
    const existingSetsForExercise = sets.filter(s => s.exerciseId === exercise.id).length;

    setSets([
      ...sets,
      {
        tempId: generateId(),
        exerciseId: exercise.id,
        exerciseName: exercise.name,
        setNumber: existingSetsForExercise + 1,
        reps: lastSet?.reps ?? 1,
        weight: lastSet?.weight ?? 2.5,
      },
    ]);

    // Save to recent exercises
    saveRecentExercise(exercise.id);
    setShowExerciseModal(false);
  };

  const updateSet = (tempId: string, field: keyof LocalSet, value: unknown) => {
    setSets(sets.map((s) => (s.tempId === tempId ? { ...s, [field]: value } : s)));
  };

  const removeSet = (tempId: string) => {
    setSets(sets.filter((s) => s.tempId !== tempId));
  };

  const duplicateSet = (tempId: string) => {
    const index = sets.findIndex((s) => s.tempId === tempId);
    if (index !== -1) {
      const original = sets[index]!;
      const newSet: LocalSet = {
        ...original,
        tempId: generateId(),
        setNumber: original.setNumber + 1,
      };
      const newSets = [...sets];
      newSets.splice(index + 1, 0, newSet);
      setSets(newSets);
    }
  };

  const exercises = exercisesData?.exercises || [];

  const groupedSets = sets.reduce((acc, set) => {
    const key = set.exerciseId;
    if (!acc[key]) {
      acc[key] = {
        exerciseName: set.exerciseName || 'Tuntematon',
        sets: [],
      };
    }
    acc[key].sets.push({ ...set });
    return acc;
  }, {} as Record<string, { exerciseName: string; sets: LocalSet[] }>);

  if (loadingWorkout) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className={cn(
            'p-3 rounded-xl',
            'hover:bg-gray-100 dark:hover:bg-gray-800',
            'active:scale-95 active:bg-gray-200 dark:active:bg-gray-700',
            'transition-all touch-manipulation'
          )}
          aria-label="Takaisin"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isEditing ? 'Muokkaa treeniä' : 'Uusi treeni'}
        </h1>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          saveMutation.mutate();
        }}
        className="space-y-6"
      >
        <div className="card p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">Perustiedot</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Päivämäärä *</label>
              <input
                type="date"
                value={startedAt}
                onChange={(e) => setStartedAt(e.target.value)}
                required
                className="input"
              />
            </div>
            <div>
              <label className="label">Nimi (valinnainen)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder={`Treeni ${new Date(startedAt).toLocaleDateString('fi-FI')}`}
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="label">
              Treenityyppi
              {!workoutType && sets.length > 0 && (
                <span className="text-gray-400 font-normal ml-1">
                  (arvio: {FILTER_WORKOUT_TYPES.find(t => t.id === inferWorkoutType())?.name})
                </span>
              )}
            </label>
            <select
              value={workoutType}
              onChange={(e) => setWorkoutType(e.target.value as FilterWorkoutType | '')}
              className="input"
            >
              <option value="">Automaattinen</option>
              {FILTER_WORKOUT_TYPES.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-4">
            <label className="label">Muistiinpanot</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="input"
              placeholder="Treenimuistiinpanot..."
            />
          </div>
        </div>

        {/* Linkitetty Garmin-aktiviteetti */}
        {existingWorkout?.wearableActivities && existingWorkout.wearableActivities.length > 0 && (
          <LinkedActivityCard activities={existingWorkout.wearableActivities} />
        )}

        {/* Sarjat-osio - näytetään aina, mutta cardio-treeneille eri viesti */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {sets.length > 0 ? `Sarjat (${sets.length})` : 'Sarjat'}
            </h2>
            <button
              type="button"
              onClick={() => setShowExerciseModal(true)}
              className="btn btn-secondary"
            >
              <Plus className="h-4 w-4" />
              Lisää sarja
            </button>
          </div>

          {sets.length === 0 ? (
            existingWorkout?.wearableActivities && existingWorkout.wearableActivities.length > 0 ? (
              // Garmin-treeni: näytä tyypin mukainen viesti
              (() => {
                const activity = existingWorkout.wearableActivities[0];
                const isStrength = activity?.activityType?.toLowerCase().includes('strength') ||
                                   activity?.activityType?.toLowerCase().includes('weight');
                return (
                  <div className={`text-center py-6 text-gray-500 rounded-lg ${
                    isStrength
                      ? 'bg-orange-50 dark:bg-orange-900/20'
                      : 'bg-primary-50 dark:bg-primary-900/20'
                  }`}>
                    <Activity className={`h-8 w-8 mx-auto mb-2 ${
                      isStrength ? 'text-orange-500' : 'text-primary-500'
                    }`} />
                    <p className="font-medium text-gray-700 dark:text-gray-300">
                      {isStrength ? 'Voimaharjoittelu' : 'Cardio-treeni'}
                    </p>
                    <p className="text-sm mt-1">Tiedot tulevat Garmin-aktiviteetista</p>
                    <button
                      type="button"
                      onClick={() => setShowExerciseModal(true)}
                      className="text-primary-600 hover:underline mt-3 text-sm"
                    >
                      {isStrength ? 'Lisää sarjat manuaalisesti?' : 'Lisää silti voimaharjoittelusarja?'}
                    </button>
                  </div>
                );
              })()
            ) : (
              // Tyhjä treeni ilman Garmin-dataa
              <div className="text-center py-8 text-gray-500">
                <p>Ei vielä sarjoja</p>
                <button
                  type="button"
                  onClick={() => setShowExerciseModal(true)}
                  className="text-primary-600 hover:underline mt-2"
                >
                  Lisää ensimmäinen sarja
                </button>
              </div>
            )
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedSets).map(([exerciseId, group]) => (
                <div key={exerciseId} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-3">{group.exerciseName}</h3>
                  <div className="space-y-4">
                    {group.sets.map((set, setIndex) => {
                      // Get previous set for this exercise (from previous workout or earlier in this workout)
                      const previousSetInWorkout = setIndex > 0 ? group.sets[setIndex - 1] : undefined;
                      const previousSet = previousSetInWorkout
                        ? { weight: previousSetInWorkout.weight, reps: previousSetInWorkout.reps }
                        : undefined;

                      return (
                        <SetRow
                          key={set.tempId}
                          setNumber={setIndex + 1}
                          weight={set.weight}
                          reps={set.reps}
                          previousSet={previousSet}
                          onWeightChange={(val) => updateSet(set.tempId, 'weight', val)}
                          onRepsChange={(val) => updateSet(set.tempId, 'reps', val)}
                          onDelete={() => removeSet(set.tempId)}
                          onDuplicate={() => duplicateSet(set.tempId)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={cn(
              'flex-1 min-h-[52px] px-4 font-medium rounded-xl',
              'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
              'transition-all duration-200 touch-manipulation',
              'hover:bg-gray-200 dark:hover:bg-gray-600',
              'active:scale-[0.98] active:bg-gray-300 dark:active:bg-gray-500'
            )}
          >
            Peruuta
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 min-h-[52px] px-4 font-semibold rounded-xl',
              'bg-primary-600 text-white',
              'transition-all duration-200 touch-manipulation',
              'hover:bg-primary-700',
              'active:scale-[0.98] active:bg-primary-800',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100'
            )}
          >
            {saveMutation.isPending ? (
              <LoadingSpinner size="sm" className="text-white" />
            ) : (
              <>
                <Save className="h-5 w-5" />
                {isEditing ? 'Tallenna' : 'Tallenna treeni'}
              </>
            )}
          </button>
        </div>
      </form>

      <ExercisePickerModal
        isOpen={showExerciseModal}
        onClose={() => setShowExerciseModal(false)}
        onSelect={addSet}
        onCreateExercise={handleCreateExercise}
        exercises={exercises}
        recentExerciseIds={recentExerciseIds}
        isCreating={createExerciseMutation.isPending}
      />
    </div>
  );
}
